import { Injectable, OnDestroy } from '@angular/core';
import { Content } from '@ngneat/overview';
import {
  HotToastService,
  ObservableMessages,
  Toast,
  ToastConfig,
} from '@ngxpert/hot-toast';
import {
  BehaviorSubject,
  Observable,
  Subject,
  delay,
  filter,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs';

export type SnackbarConfigParams = Toast<any>;

export interface SnackBarQueueItem {
  message: Content;
  beingDispatched: boolean;
  config?: Partial<SnackbarConfigParams>;
}

const MAX_SNACKBAR_QUEUE = 1;

@Injectable({
  providedIn: 'root',
})
export class SnackbarService implements OnDestroy {
  private _defaultConfig = new ToastConfig();
  private readonly _snackBarQueue = new BehaviorSubject<SnackBarQueueItem[]>(
    [],
  );
  private readonly _snackBarQueue$ = this._snackBarQueue.asObservable();
  private readonly ngDestroy = new Subject();

  constructor(private _snackBar: HotToastService) {
    /* Dispatches all queued snack bars one by one */
    this._snackBarQueue$
      .pipe(
        filter((queue) => queue.length > 0 && !queue[0].beingDispatched),
        tap(() => {
          const updatedQueue = this._snackBarQueue.value;
          updatedQueue[0].beingDispatched = true;
          this._snackBarQueue.next(updatedQueue);
        }),
        map((queue) => queue[0]),
        takeUntil(this.ngDestroy),
      )
      .subscribe((snackBarItem) =>
        this.queueSnackBar(snackBarItem.message, snackBarItem.config),
      );
  }

  ngOnDestroy() {
    this._snackBarQueue.next([]);
    this._snackBarQueue.complete();
    this.ngDestroy.next(true);
    this.ngDestroy.complete();
  }

  showSnackbar(message: Content, config?: Partial<SnackbarConfigParams>) {
    this._snackBarQueue.next(
      this._snackBarQueue.value.concat([
        { message, config, beingDispatched: false },
      ]),
    );
  }

  showSnackbarError(message: Content, config?: Partial<SnackbarConfigParams>) {
    this.showSnackbar(message, {
      ...config,
      type: 'error',
    });
  }

  showSnackbarSuccess(
    message: Content,
    config?: Partial<SnackbarConfigParams>,
  ) {
    this.showSnackbar(message, {
      ...config,
      type: 'success',
    });
  }

  showSnackbarWarning(
    message: Content,
    config?: Partial<SnackbarConfigParams>,
  ) {
    this.showSnackbar(message, {
      ...config,
      type: 'warning',
    });
  }

  showSnackbarInfo(message: Content, config?: Partial<SnackbarConfigParams>) {
    this.showSnackbar(message, {
      ...config,
      type: 'info',
    });
  }

  observe<T>(messages: ObservableMessages<T, any>) {
    return this._snackBar.observe(messages);
  }

  private queueSnackBar(
    message: Content,
    config?: Partial<SnackbarConfigParams>,
  ) {
    const duration = this.getDuration(config);
    this.removeDismissedSnackBar(
      this._snackBar.show(message, {
        duration,
        ...config,
      }).afterClosed,
    );
  }

  /* Remove dismissed snack bar from queue and triggers another one to appear */
  private removeDismissedSnackBar(dismissed: Observable<any>) {
    dismissed.pipe(delay(500), take(MAX_SNACKBAR_QUEUE)).subscribe(() => {
      const updatedQueue = this._snackBarQueue.value;
      if (updatedQueue.length && updatedQueue[0].beingDispatched)
        updatedQueue.shift();
      this._snackBarQueue.next(updatedQueue);
    });
  }

  private getDuration(config?: Partial<SnackbarConfigParams>): number {
    if (config?.duration) return config.duration;
    else return 3000;
  }
}
